import { z } from 'zod';

/** Every id in the data model is a cuid (except Session.id which is 32-byte hex). */
export const idSchema = z.string().min(1).max(64);
export type Id = z.infer<typeof idSchema>;

/** JSON-serialised `Date` (what Nest puts on the wire). */
export const dateTimeSchema = z.string().datetime({ offset: true });
export type DateTimeString = z.infer<typeof dateTimeSchema>;

export const nullableDateTimeSchema = dateTimeSchema.nullable();

/** Recursive JSON value — used for `metadata` / `scope` / `totals` blobs. */
export type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };
export const jsonValueSchema: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(jsonValueSchema),
    z.record(jsonValueSchema),
  ]),
);

export const jsonObjectSchema = z.record(jsonValueSchema);
export type JsonObject = z.infer<typeof jsonObjectSchema>;

/** Passthrough CSV columns: header name -> raw cell value. */
export const extraValuesSchema = z.record(z.string());
export type ExtraValues = z.infer<typeof extraValuesSchema>;

/** 1-based page + size. Query strings are coerced. */
export const paginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export type PaginationQuery = z.infer<typeof paginationQuerySchema>;

export const paginationMetaSchema = z.object({
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
  total: z.number().int().min(0),
  hasMore: z.boolean(),
});
export type PaginationMeta = z.infer<typeof paginationMetaSchema>;

export function paginatedSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
    total: z.number().int().min(0),
    hasMore: z.boolean(),
  });
}

export interface Paginated<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
  hasMore: boolean;
}

/** Uniform error envelope thrown by the API (Nest HttpException shape + our extras). */
export const apiErrorSchema = z.object({
  statusCode: z.number().int(),
  error: z.string(),
  message: z.union([z.string(), z.array(z.string())]),
  /** Machine-readable code, e.g. `row_version_conflict`, `elevenlabs_quota`. */
  code: z.string().optional(),
  details: jsonValueSchema.optional(),
});
export type ApiError = z.infer<typeof apiErrorSchema>;

export const okSchema = z.object({ ok: z.literal(true) });
export type Ok = z.infer<typeof okSchema>;

export const countSchema = z.object({ count: z.number().int().min(0) });
export type CountResponse = z.infer<typeof countSchema>;
